// ACDEV-25945: distinguish a genuine Apple Pay user-cancel from a
// merchant-validation / domain failure. Both reach Adyen's `onError` as the
// SAME `CANCEL` (Apple fires `oncancel` when the user dismisses the sheet AND
// when PassKit rejects the merchant session), so the error payload alone can't
// tell them apart. We capture a signal upstream: Adyen's ApplePay component
// calls the native `ApplePaySession.prototype.completeMerchantValidation(session)`
// with the Apple-signed merchant session. We wrap that once to snapshot the
// session's `domainName` and when it happened, then classify the later CANCEL.

interface MerchantSessionLike {
  domainName?: string;
}

type ApplePaySessionProto = {
  completeMerchantValidation: (merchantSession: MerchantSessionLike) => void;
};

interface MerchantValidationSnapshot {
  at: number;
  sessionDomain?: string;
  groundTruthDomain: string | null;
  domainMismatch: boolean;
}

let snapshot: MerchantValidationSnapshot | null = null;
let installed = false;

// The origin PassKit actually validates the merchant session against is the
// TOP-LEVEL browsing context. `location.ancestorOrigins` reports it and is NOT
// stripped in Safari Private Browsing (unlike `document.referrer`). Returns null
// when unavailable (e.g. Firefox, or a top-level document) so callers degrade to
// the timing heuristic instead of comparing against a wrong value.
const getGroundTruthTopLevelDomain = (): string | null => {
  try {
    const origins = window.location.ancestorOrigins;
    if (origins && origins.length > 0) {
      return new URL(origins[origins.length - 1]).hostname;
    }
  } catch {
    // ignore malformed origins / unsupported API
  }
  return null;
};

// Wrap `completeMerchantValidation` once. Idempotent and safe to call on every
// Apple Pay init; no-ops outside Safari where ApplePaySession is absent.
export const installApplePayMerchantValidationProbe = (): void => {
  if (installed) return;
  const applePaySession = (
    window as unknown as { ApplePaySession?: { prototype: ApplePaySessionProto } }
  ).ApplePaySession;
  const proto = applePaySession?.prototype;
  if (!proto?.completeMerchantValidation) return;
  installed = true;

  const original = proto.completeMerchantValidation;
  proto.completeMerchantValidation = function (
    this: unknown,
    merchantSession: MerchantSessionLike,
  ) {
    const groundTruthDomain = getGroundTruthTopLevelDomain();
    const sessionDomain = merchantSession?.domainName;
    snapshot = {
      at: Date.now(),
      sessionDomain,
      groundTruthDomain,
      domainMismatch: !!sessionDomain && !!groundTruthDomain && sessionDomain !== groundTruthDomain,
    };
    return original.call(this, merchantSession);
  };
};

export type ApplePayCancelClassification =
  | 'domain_mismatch'
  | 'merchant_validation_rejected'
  | 'user_cancelled'
  | 'unknown';

export interface ApplePayCancelDiagnostics {
  classification: ApplePayCancelClassification;
  sessionDomain?: string;
  groundTruthDomain?: string | null;
  msSinceMerchantValidation?: number;
}

// A human cannot read and dismiss the Apple Pay sheet faster than this, so a
// CANCEL arriving within the window after a completed merchant validation is
// PassKit tearing the sheet down (rejected session), not a user action.
const PASSKIT_REJECTION_WINDOW_MS = 800;

export const classifyApplePayCancel = (): ApplePayCancelDiagnostics => {
  if (!snapshot) {
    return { classification: 'unknown' };
  }
  const msSinceMerchantValidation = Date.now() - snapshot.at;
  const details = {
    sessionDomain: snapshot.sessionDomain,
    groundTruthDomain: snapshot.groundTruthDomain,
    msSinceMerchantValidation,
  };
  if (snapshot.domainMismatch) {
    return { classification: 'domain_mismatch', ...details };
  }
  if (msSinceMerchantValidation <= PASSKIT_REJECTION_WINDOW_MS) {
    return { classification: 'merchant_validation_rejected', ...details };
  }
  return { classification: 'user_cancelled', ...details };
};

// Clear the snapshot at the start of each Apple Pay session so a stale value
// from a previous sheet can't misclassify a later cancel.
export const resetApplePayCancelDiagnostics = (): void => {
  snapshot = null;
};
